// Paste confirm for the area plan grid.
//
// Two jobs: show the planner the before -> after time of every block so a paste
// is never a surprise, and refuse the paste outright when anything already
// occupies those hours. Collisions are not resolved here -- no shifting, no
// stacking, no overwriting. The planner is told what is in the way and sent to
// move it first, and the clipboard survives so they can come straight back.
import React from 'react';
import { Modal, DatePicker, Button, Alert } from 'antd';
import dayjs from 'dayjs';

const ACTIVITY_LABEL = {
  manufacturing: 'Operation',
  cleaning: 'Cleaning',
  maintenance: 'Maintenance',
};

const styles = {
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '8px',
    alignItems: 'baseline',
    padding: '6px 8px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '12px',
  },
  label: { color: '#111827', fontWeight: 500 },
  was: { color: '#9ca3af', textDecoration: 'line-through', fontSize: '11px' },
  now: { color: '#111827', fontVariantNumeric: 'tabular-nums' },
  blockList: {
    maxHeight: '220px',
    overflow: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    marginTop: '8px',
  },
  field: { marginBottom: '12px' },
  fieldLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '4px',
  },
  blocker: {
    fontSize: '12px',
    fontVariantNumeric: 'tabular-nums',
    padding: '2px 0',
  },
};

const fmt = (value) => (value ? dayjs(value).format('DD-MMM HH:mm') : 'open-ended');

const describe = (block) => {
  const kind = ACTIVITY_LABEL[block.activity_type] || block.activity_type;
  const detail = block.batch_number || block.cleaning_type || block.maintenance_type || '';
  return detail ? `${kind} - ${detail}` : kind;
};

const AreaPlanPasteModal = ({
  open,
  planned,          // result of planPaste(): { op, blocks, collisions, outOfWeek }
  targetAreaName,
  targetTime,
  onTargetTimeChange,
  onConfirm,
  onClose,
  pasting,
}) => {
  if (!planned) return null;

  const { op, blocks, collisions, outOfWeek } = planned;
  const blocked = collisions.length > 0;

  // One activity can block several pasted blocks; list each blocker once.
  const blockers = [];
  const seen = new Set();
  collisions.forEach(({ blockedBy }) => {
    blockedBy.forEach((activity) => {
      if (seen.has(activity.id)) return;
      seen.add(activity.id);
      blockers.push(activity);
    });
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      destroyOnClose
      title={`${op === 'cut' ? 'Move' : 'Copy'} ${blocks.length} ${
        blocks.length === 1 ? 'activity' : 'activities'
      } to ${targetAreaName}`}
      footer={
        blocked
          ? [
              <Button key="close" onClick={onClose}>
                Close
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onClose}>
                Cancel
              </Button>,
              <Button key="ok" type="primary" loading={pasting} onClick={onConfirm}>
                {op === 'cut' ? 'Move here' : 'Copy here'}
              </Button>,
            ]
      }
    >
      <div style={styles.field}>
        <label style={styles.fieldLabel}>Paste at</label>
        <DatePicker
          showTime={{ format: 'HH:mm' }}
          format="DD-MMM-YYYY HH:mm"
          value={targetTime ? dayjs(targetTime) : null}
          onChange={onTargetTimeChange}
          allowClear={false}
          style={{ width: '100%' }}
        />
      </div>

      {blocked && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: '12px' }}
          message={`Cannot paste into ${targetAreaName} at ${fmt(targetTime)}`}
          description={
            <div>
              <div style={{ marginBottom: '6px' }}>
                These activities are already planned in those hours:
              </div>
              {blockers.map((activity) => (
                <div key={activity.id} style={styles.blocker}>
                  &bull; {describe({
                    activity_type: activity.activity_type,
                    batch_number: activity.batch_number,
                    cleaning_type: activity.cleaning_type,
                    maintenance_type: activity.maintenance_type,
                  })}
                  {'  '}
                  {fmt(activity.start_time)} &rarr; {fmt(activity.end_time)}
                </div>
              ))}
              <div style={{ marginTop: '8px' }}>
                Move or delete them first, then paste again. Your {blocks.length}{' '}
                {op === 'cut' ? 'cut' : 'copied'}{' '}
                {blocks.length === 1 ? 'activity is' : 'activities are'} still held.
              </div>
            </div>
          }
        />
      )}

      {!blocked && outOfWeek.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: '12px' }}
          message={`${outOfWeek.length} ${
            outOfWeek.length === 1 ? 'block lands' : 'blocks land'
          } after this week`}
          description={`Saved, but navigate to the week of ${dayjs(outOfWeek[0].newStart).format(
            'DD-MMM'
          )} to see ${outOfWeek.length === 1 ? 'it' : 'them'}.`}
        />
      )}

      <div style={styles.blockList}>
        {blocks.map((block, index) => (
          <div key={block.sourceId ?? index} style={styles.row}>
            <span style={styles.label}>{describe(block)}</span>
            <span>
              <span style={styles.was}>
                {fmt(block.start)} &rarr; {fmt(block.end)}
              </span>
              <br />
              <span style={styles.now}>
                {fmt(block.newStart)} &rarr; {fmt(block.newEnd)}
              </span>
            </span>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default AreaPlanPasteModal;
